import { formatStackFrames, ICoreUtils, IStackLocation, parseStackLocation } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppErrorReport - the browser app's error handler: the `ICoreUtils` that `getCoreUtils()` answers once
// `AppErrorInstall` has run (2026-09-28).
//
// ==================================================================================================
// !! ⛔ WHY (P0, owner 2026-09-28: "errors in the browser app should be reported to the global error
// !!    handler. That is a fundamental part of the typescript coding skills and requirements.")
// ==================================================================================================
//
// Every one of the app's ~1,300 `getCoreUtils().KGlobalErrorHandler(e, ...)` calls went to
// `kCoreHandler`'s `DefaultCoreUtils` - a console line and NOTHING ELSE: no report, and no `KErrorMode` latch
// (its gate never flips). `kCoreHandler` answers that default until the HOST calls `SetCoreUtils(...)` at
// startup; in every other browser product `kalmstromShared4` does it (`SetCoreUtils(ks)` at module scope), and
// this app does not load `kalmstromShared4` - so no browser error of WACommander's had ever been reported.
//
// ⚠ WHY NOT `ks` ITSELF: `ks.KGlobalErrorHandler` opens a SharePoint-style modal (`ConfirmYesNo`, jQuery,
// LNG phrases) and only POSTs when the user types an e-mail address into it (`ks._ReportData` returns early
// without one) - wrong for a static page on a phone, and it would drag the SharePoint UI stack into the bundle.
// No non-SharePoint browser handler exists in `Admin` (searched: every `implements ICoreUtils` outside tests is
// `kFunctionErrorHandler`, whose report is labelled "Azure Function (Node)" and carries an invocation id).
// So this is the minimal browser `ICoreUtils`, reusing every shared part there is: `kCore.KReportError` (the
// POST to `kCore.KServicesURL`, 5-minute de-dup, version stamp - injected as the sink), `parseStackLocation`
// and `formatStackFrames` (the same frame parsing the other hosts use), and the SAME payload shape the browser
// `ks._ReportData` and `kFunctionErrorHandler.BuildReport` send: ProductName, FunctionName, ErrorFileName,
// ErrorNumber (a NUMBER - the flow's line field is numeric), ErrorDescription, Comments, ClientName,
// ClientEmail, OSVersion (the user agent, as `ks` sends).
//
// ⛔ PRIVACY: THE REPORT LEAVES FOR A POWER AUTOMATE FLOW AND THE ESTATE'S kErrors STORE, SO IT CARRIES CODE
// COORDINATES, NEVER THE SUBSCRIBER'S DATA. The page address is origin + path only - the HASH holds a contact's
// page href (`#c/People%2FAnna...`), so it is reduced to its route WORD (`c`, `tasks`, `signup`). Every text
// that could quote input (the description, the caller's detail, the stack) goes through `Redact`: runs of 7+
// digits (phone numbers, lids), `sk-ant-` tokens, `People/<folder>` paths (to the end of the line - folder names
// have spaces) and the query/hash of any URL are replaced. No message text, contact name, number, key or ticket is ever put in by this class.
//
// ⭐ THE GATE: the first report LATCHES `KErrorMode` (the designed signal - every guarded method becomes a no-op
// for the rest of the visit) and shows ONE line saying so (`IAppErrorShow`), because a page that silently stops
// answering clicks is worse than one that says "reload". Later errors are console lines only.
// ⚠ Expected conditions (a missing file, a refused ticket, a lost connection, a file that does not parse) must
// therefore NEVER reach this handler - they are fate 1, answered where they happen.

/** Where the page is and what it runs in - the browser's `location` and `navigator`, or a test's fake. */
export interface IAppErrorPage {
    Href(): string;
    Agent(): string;
}

/** Where a finished report goes: `kCore.KReportError` in the browser, a recorder in a test. */
export interface IAppErrorSink {
    Send(Data: Record<string, unknown>): void;
}

/** The handler's one visible line ("something went wrong - reload"), or nothing in a test. */
export interface IAppErrorShow {
    Show(Text: string): void;
}

/** A browser `ErrorEvent` / `PromiseRejectionEvent`, as far as this reads one. */
export interface IAppErrorEvent {
    readonly error?: unknown;
    readonly reason?: unknown;
    readonly message?: string;
}

export class AppErrorReportRules {
    public static readonly Product: string = "WACommander";
    public static readonly Client: string = "WACommander app (browser)";
    public static readonly ShownText: string = "Something went wrong in WACommander and it has been reported. "
        + "Reload the page to carry on.";
    public static readonly MaxDescription: number = 1000;
    public static readonly MaxStack: number = 2000;
    /** A `People/<folder>...` path (or its encoded `People%2F` form) up to the end of the line. ⚠ Built from a string:
     *  the edit tooling has turned `\n` inside a regex LITERAL into a raw line break before (CLAUDE.md `HeaderSafe`). */
    public static readonly FolderPattern: string = "People(?:/|%2F)[^\\n\"'<>()]*";

    /**
     * The page as a report may name it: origin + path, never the query or the hash.
     * DOCUMENTED EXCEPTION: pure formatter called from INSIDE the error handler - it must never re-enter it.
     * ERROR-SUPPRESSED-JUSTIFIED: an address that does not parse is reported as "" (nothing leaks).
     */
    public static PageOf = (Href: string): string => {
        try {
            const Url: URL = new URL(String(Href));
            if (Url.protocol === "blob:" || Url.protocol === "data:") { return Url.protocol; }
            return Url.origin + Url.pathname;
        } catch (e) {
            return "";
        }
    };

    /**
     * The route WORD of the address's hash (`#c/People%2F...` -> `c`), or "" - the rest names a contact.
     * DOCUMENTED EXCEPTION: pure formatter inside the handler. ERROR-SUPPRESSED-JUSTIFIED: unreadable = "".
     */
    public static RouteOf = (Href: string): string => {
        try {
            const At: number = String(Href).indexOf("#");
            if (At < 0) { return ""; }
            const Word: string = String(Href).substring(At + 1).split("/")[0];
            if (/^[a-z][a-z0-9-]{0,23}$/i.test(Word) === false) { return ""; }
            return Word;
        } catch (e) {
            return "";
        }
    };

    /**
     * Removes what could be the subscriber's data from a text before it leaves the page.
     * DOCUMENTED EXCEPTION: pure formatter inside the handler. ERROR-SUPPRESSED-JUSTIFIED: a redaction that
     * fails WITHHOLDS the text rather than sending it raw - the raw text is what it was called to clean.
     */
    public static Redact = (Text: string): string => {
        try {
            let Out: string = String(Text);
            Out = Out.replace(/\b(?:https?|file|blob):\/\/[^\s"'<>()]+/gi, AppErrorReportRules.UrlOnly);
            // ⚠ To the END OF THE LINE: a folder name has spaces ("People/Anna Svensson/..."), so no shorter cut is safe.
            Out = Out.replace(new RegExp(AppErrorReportRules.FolderPattern, "gi"), "People/<contact>");
            Out = Out.replace(/sk-ant-[A-Za-z0-9_-]+/g, "<token>");
            Out = Out.replace(/\+?\d{7,}/g, "<digits>");
            return Out;
        } catch (e) {
            return "<redaction failed - text withheld>";
        }
    };

    /** A URL in a text, cut before its query or hash. DOCUMENTED EXCEPTION: a `replace` callback of `Redact`. */
    private static UrlOnly = (Found: string): string => {
        let End: number = Found.length;
        const Query: number = Found.indexOf("?");
        const Hash: number = Found.indexOf("#");
        if (Query >= 0 && Query < End) { End = Query; }
        if (Hash >= 0 && Hash < End) { End = Hash; }
        return Found.substring(0, End);
    };

    /**
     * Anything thrown or rejected, as an Error. DOCUMENTED EXCEPTION: pure, inside the handler.
     * ERROR-SUPPRESSED-JUSTIFIED: a value whose conversion throws (a hostile `toString`) becomes a fixed text.
     */
    public static ErrorOf = (Value: unknown): Error => {
        try {
            if (Value instanceof Error) { return Value; }
            if (Value !== null && typeof Value === "object") {
                const Bag: Record<string, unknown> = Value as Record<string, unknown>;
                if (typeof Bag.message === "string" && Bag.message.length > 0) {
                    const Made: Error = new Error(Bag.message);
                    if (typeof Bag.stack === "string") { Made.stack = Bag.stack; }
                    return Made;
                }
            }
            return new Error(String(Value));
        } catch (e) {
            return new Error("A value was thrown that cannot be described.");
        }
    };

    /**
     * The report, in the shape the other hosts send (see the header). Every text is redacted.
     * DOCUMENTED EXCEPTION: a constant shape over values in hand, built inside the handler.
     */
    public static Payload = (Value: unknown, Location: string, Detail: string, Href: string, Agent: string,
        Version: string): Record<string, unknown> => {
        const Err: Error = AppErrorReportRules.ErrorOf(Value);
        const Where: IStackLocation = parseStackLocation(Err.stack);
        let Frames: string = formatStackFrames(Err.stack);
        if (Frames === "") { Frames = "none"; }
        let Description: string = Err.message;
        if (Err.name !== undefined && Err.name !== "" && Err.name !== "Error") { Description = Err.name + ": " + Description; }
        Description = AppErrorReportRules.Redact(Description).substring(0, AppErrorReportRules.MaxDescription);
        return {
            ProductName: AppErrorReportRules.Product + " " + Version,
            FunctionName: String(Location),
            ErrorFileName: Where.FileName,
            ErrorNumber: Where.LineNumber,
            ErrorDescription: Description,
            Comments: AppErrorReportRules.Comments(Where, Frames, Detail, Href),
            ClientName: AppErrorReportRules.Client,
            ClientEmail: "",
            OSVersion: String(Agent),
        };
    };

    /** Source first (read first even when a sink flattens the lines), then the page, then the stack.
     *  DOCUMENTED EXCEPTION: pure string composition inside the handler. */
    private static Comments = (Where: IStackLocation, Frames: string, Detail: string, Href: string): string => {
        let Out: string = "";
        if (Where.FileName !== "") { Out = "Source: " + Where.FileName + ":" + String(Where.LineNumber) + "\n"; }
        let Route: string = AppErrorReportRules.RouteOf(Href);
        if (Route === "") { Route = "(none)"; }
        Out = Out + "Page: " + AppErrorReportRules.PageOf(Href) + " route " + Route;
        if (Detail !== undefined && Detail !== null && String(Detail) !== "") {
            Out = Out + "\nDetail: " + AppErrorReportRules.Redact(String(Detail)).substring(0, 500);
        }
        return Out + "\nStack:\n" + AppErrorReportRules.Redact(Frames).substring(0, AppErrorReportRules.MaxStack);
    };
}

/**
 * The app's `ICoreUtils`.
 *
 * DOCUMENTED EXCEPTION TO THE ERROR PATTERN, for `kFunctionErrorHandler`'s reasons: this class IS the handler.
 * It owns the gate it would be asked to consult (a guard reading `KErrorMode` before reporting would report
 * nothing, ever), and a catch here has nowhere left to report to - so each fallible step carries its own
 * `ERROR-SUPPRESSED-JUSTIFIED` catch and degrades instead of throwing out of the handler.
 */
export class AppErrorReporter implements ICoreUtils {
    public KErrorMode: boolean = false;
    /** How many reports were handed to the sink (at most one per visit - the first latches the gate). */
    public Reported: number = 0;
    private Page: IAppErrorPage;
    private Sink: IAppErrorSink;
    private Shown: IAppErrorShow;
    private Version: string;

    public constructor(Page: IAppErrorPage, Sink: IAppErrorSink, Shown: IAppErrorShow, Version: string) {
        this.Page = Page;
        this.Sink = Sink;
        this.Shown = Shown;
        this.Version = Version;
    }

    public KGlobalErrorHandler = (e: unknown, location: string, additionalMessage?: string): void => {
        if (this.KErrorMode === true) {
            this.Console("[WACommander] also in " + String(location) + " (not reported - an earlier error is being reported): ", e);
            return;
        }
        this.KErrorMode = true;
        let Detail: string = "";
        if (additionalMessage !== undefined && additionalMessage !== null) { Detail = String(additionalMessage); }
        this.Console("[WACommander] error in " + String(location) + ": ", e);
        const Data: Record<string, unknown> | null = this.Build(e, String(location), Detail);
        if (Data !== null) { this.Deliver(Data); }
        this.Tell();
    };

    /** window `error` - an exception nothing caught. DOCUMENTED EXCEPTION: handler entry point (no guard - the
     *  handler decides what a latched gate means). */
    public OnWindowError = (Ev: IAppErrorEvent): void => {
        let Value: unknown = null;
        if (Ev !== null && Ev !== undefined) { Value = Ev.error; }
        if ((Value === null || Value === undefined) && Ev !== null && Ev !== undefined) { Value = String(Ev.message); }
        this.KGlobalErrorHandler(Value, "window.error");
    };

    /** window `unhandledrejection` - a promise nobody answered. DOCUMENTED EXCEPTION: handler entry point. */
    public OnRejection = (Ev: IAppErrorEvent): void => {
        let Value: unknown = null;
        if (Ev !== null && Ev !== undefined) { Value = Ev.reason; }
        this.KGlobalErrorHandler(Value, "window.unhandledrejection");
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: a fault while BUILDING the report must not throw out of the handler; the
     *  console line above already holds the original error. */
    private Build = (e: unknown, Location: string, Detail: string): Record<string, unknown> | null => {
        try {
            return AppErrorReportRules.Payload(e, Location, Detail, this.Page.Href(), this.Page.Agent(), this.Version);
        } catch (Failed) {
            this.Console("[WACommander] the error report could not be built: ", Failed);
            return null;
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: the report itself failed - there is nowhere left to report that to. */
    private Deliver = (Data: Record<string, unknown>): void => {
        try {
            this.Reported = this.Reported + 1;
            this.Sink.Send(Data);
        } catch (Failed) {
            this.Console("[WACommander] the error report could not be sent: ", Failed);
        }
    };

    /** ERROR-SUPPRESSED-JUSTIFIED: the notice is a courtesy; a page that cannot draw it still has the report. */
    private Tell = (): void => {
        try {
            this.Shown.Show(AppErrorReportRules.ShownText);
        } catch (Failed) {
            this.Console("[WACommander] the error notice could not be shown: ", Failed);
        }
    };

    /** The handler's own console line (its logging job, not a swallow). ERROR-SUPPRESSED-JUSTIFIED: a console
     *  that throws has nothing behind it. */
    private Console = (Lead: string, Value: unknown): void => {
        try {
            console.error(Lead, Value);
        } catch (Failed) {
            return;
        }
    };
}
